import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Sparkles, Loader2, Send, CheckCircle2, Video, BookOpen } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";

function MessageCard({ msg, onReplied }) {
  const navigate = useNavigate();
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);
  const [startingInterview, setStartingInterview] = useState(false);
  const [replyText, setReplyText] = useState(msg.reply_text || "");
  const [showReplyBox, setShowReplyBox] = useState(false);

  const draftReply = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/messages/${msg.id}/draft-reply`);
      setReplyText(data.draft);
      setShowReplyBox(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate a draft reply");
    } finally {
      setDrafting(false);
    }
  };

  const startInvitedInterview = async () => {
    setStartingInterview(true);
    try {
      const { data } = await api.post(`/interview/start-from-invite/${msg.id}`);
      navigate(`/interview/session/${data.session_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start this interview");
    } finally {
      setStartingInterview(false);
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) {
      toast.error("Reply cannot be empty");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/messages/${msg.id}/reply`, { reply_text: replyText });
      onReplied(data);
      toast.success("Reply sent");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass-panel rounded-xl p-5" data-testid={`message-card-${msg.id}`}>
      <p className="text-sm font-semibold text-white">{msg.recruiter_name} <span className="text-indigo-400">· {msg.company_name}</span></p>
      <p className="text-sm text-slate-300 mt-2">{msg.message}</p>

      {msg.interview_invite && (
        <div>
          {msg.interview_invite.knowledge_base_name && (
            <p className="flex items-center gap-1.5 text-[11px] text-indigo-300 mt-3" data-testid={`message-kb-badge-${msg.id}`}>
              <BookOpen className="w-3 h-3" /> Uses {msg.company_name}'s own question set: {msg.interview_invite.knowledge_base_name}
            </p>
          )}
          <button
            onClick={startInvitedInterview}
            disabled={startingInterview}
            data-testid={`message-start-interview-btn-${msg.id}`}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-gradient-to-r from-purple-500 to-indigo-500 hover:opacity-90 disabled:opacity-60 px-3.5 py-2 rounded-lg transition-opacity"
          >
            {startingInterview ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />} Start Interview
          </button>
        </div>
      )}

      {msg.reply_text ? (
        <div className="mt-4 pt-3 border-t border-white/10 flex items-start gap-2" data-testid={`message-reply-sent-${msg.id}`}>
          <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-xs text-emerald-400 font-semibold">Your reply</p>
            <p className="text-sm text-slate-300 mt-1">{msg.reply_text}</p>
          </div>
        </div>
      ) : (
        <div className="mt-4 pt-3 border-t border-white/10">
          {!showReplyBox ? (
            <button
              onClick={draftReply}
              disabled={drafting}
              data-testid={`message-draft-reply-btn-${msg.id}`}
              className="flex items-center gap-1.5 text-xs font-semibold text-indigo-300 hover:text-indigo-200"
            >
              {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {drafting ? "Drafting..." : "Draft AI Reply"}
            </button>
          ) : (
            <div>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                rows={3}
                data-testid={`message-reply-textarea-${msg.id}`}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white resize-none focus:outline-none focus:ring-1 focus:ring-indigo-400"
              />
              <button
                onClick={sendReply}
                disabled={sending}
                data-testid={`message-send-reply-btn-${msg.id}`}
                className="mt-2 flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Send Reply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MessagesPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/messages/mine").then((r) => setMessages(r.data)).finally(() => setLoading(false));
  }, []);

  const handleReplied = (updated) => {
    setMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="messages-loading">
        <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="messages-page">
      <div className="mb-8 flex items-center gap-2">
        <Mail className="w-5 h-5 text-indigo-400" />
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">Recruiter Messages</h1>
      </div>

      {messages.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center" data-testid="messages-empty-state">
          <Mail className="w-8 h-8 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No messages yet — recruiters will reach out here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {messages.map((m) => (
            <MessageCard key={m.id} msg={m} onReplied={handleReplied} />
          ))}
        </div>
      )}
    </div>
  );
}
